/** XeAgents.ai **/
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  AlertCircle,
  CheckCircle2,
  Info,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSupportChat, SupportChatMessage } from "@/hooks/useSupportChat";
import { cn } from "@/lib/utils";

const confidenceColors = {
  high: "bg-green-500/10 text-green-600 border-green-500/20",
  medium: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20",
  low: "bg-orange-500/10 text-orange-600 border-orange-500/20",
  none: "bg-red-500/10 text-red-600 border-red-500/20",
};

const confidenceIcons = {
  high: CheckCircle2,
  medium: Info,
  low: AlertCircle,
  none: AlertCircle,
};

function FormattedContent({ content }: { content: string }) {
  const lines = content.split("\n").filter((l) => l.trim());

  return (
    <div className="space-y-1.5">
      {lines.map((line, index) => {
        const t = line.trim();
        const isBullet = /^[•\-\*]+\s/.test(t);
        const isNumbered = /^\d+[\.\)]\s/.test(t);
        const isHeader = t.endsWith(":") && t.length < 50;

        if (isBullet) {
          const text = t.replace(/^[•\-\*]+\s*/, "");
          return (
            <div key={index} className="flex items-start gap-2">
              <span className="text-primary mt-0.5 flex-shrink-0 text-xs">●</span>
              <span className="leading-relaxed">{text}</span>
            </div>
          );
        }

        if (isNumbered) {
          const match = t.match(/^(\d+[\.\)])\s*(.*)$/);
          if (match) {
            return (
              <div key={index} className="flex items-start gap-2">
                <span className="text-primary font-semibold flex-shrink-0 min-w-[1.25rem] text-xs">
                  {match[1]}
                </span>
                <span className="leading-relaxed">{match[2]}</span>
              </div>
            );
          }
        }

        if (isHeader) {
          return (
            <div key={index} className="font-semibold text-foreground mt-2 first:mt-0 text-xs uppercase tracking-wide opacity-70">
              {t}
            </div>
          );
        }

        return <div key={index} className="leading-relaxed">{t}</div>;
      })}
    </div>
  );
}

function MessageBubble({ message }: { message: SupportChatMessage }) {
  const { t } = useTranslation();
  const isUser = message.role === "user";
  const ConfidenceIcon = message.confidence ? confidenceIcons[message.confidence] : null;
  // Only show badge for low/none confidence — high/medium is expected
  const showBadge = !isUser && !message.isStreaming && message.confidence && ["low", "none"].includes(message.confidence);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className={cn("flex gap-2.5 mb-4", isUser ? "flex-row-reverse" : "flex-row")}
    >
      {/* Avatar */}
      <div
        className={cn(
          "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm",
          isUser ? "bg-primary" : "bg-gradient-to-br from-violet-500/20 to-primary/20"
        )}
      >
        {isUser ? (
          <User className="w-3.5 h-3.5 text-primary-foreground" />
        ) : (
          <Bot className="w-3.5 h-3.5 text-primary" />
        )}
      </div>

      <div className={cn("flex flex-col max-w-[78%]", isUser ? "items-end" : "items-start")}>
        {/* Bubble */}
        <div
          className={cn(
            "rounded-2xl px-3.5 py-2.5 text-sm shadow-sm",
            isUser
              ? "bg-primary text-primary-foreground rounded-tr-sm"
              : "bg-muted/80 text-foreground rounded-tl-sm border border-border/40"
          )}
        >
            {!isUser && message.isStreaming && !message.content ? (
            <div className="flex items-center gap-1.5 py-0.5">
              {[0, 0.15, 0.3].map((delay, i) => (
                <motion.span
                  key={i}
                  className="w-2 h-2 rounded-full bg-muted-foreground/50 block"
                  animate={{ y: [0, -5, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity, delay }}
                />
              ))}
            </div>
          ) : !isUser ? (
            <>
              <FormattedContent content={message.content} />
              {message.isStreaming && (
                <span className="inline-block w-0.5 h-3.5 bg-current ml-1 animate-pulse rounded-full" />
              )}
            </>
          ) : (
            <span className="leading-relaxed">{message.content}</span>
          )}
        </div>

        {/* Confidence badge — only for low/none */}
        {showBadge && (
          <Badge
            variant="outline"
            className={cn("text-[10px] mt-1.5 px-2 py-0.5", confidenceColors[message.confidence!])}
          >
            {ConfidenceIcon && <ConfidenceIcon className="w-2.5 h-2.5 mr-1" />}
            {t(`supportChat.confidence.${message.confidence}`)}
          </Badge>
        )}

        {/* Timestamp */}
        <span className="text-[10px] text-muted-foreground/60 mt-1 px-0.5">
          {message.timestamp.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
    </motion.div>
  );
}

export function SupportChatWidget() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { messages, isLoading, error, statusLoading, sendMessage, clearHistory } = useSupportChat();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputValue.trim() && !isLoading) {
      sendMessage(inputValue);
      setInputValue("");
    }
  };

  if (statusLoading) return null;

  return (
    <>
      {/* Floating toggle button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 24 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="relative">
                    {/* Pulse ring */}
                    <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
                    <Button
                      size="lg"
                      className="relative h-14 w-14 rounded-full shadow-xl shadow-primary/25"
                      onClick={() => setIsOpen(true)}
                    >
                      <MessageCircle className="h-6 w-6" />
                    </Button>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="left">
                  <p>{t("supportChat.title")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.94 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.94 }}
            transition={{ type: "spring", stiffness: 320, damping: 28 }}
            className="fixed bottom-6 right-6 z-50 w-[390px] max-w-[calc(100vw-2rem)]"
          >
            <div className="rounded-2xl shadow-2xl shadow-black/15 border border-border/60 overflow-hidden bg-background flex flex-col">

              {/* Header */}
              <div className="bg-gradient-to-r from-primary to-violet-600 px-4 py-3.5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
                      <Bot className="w-5 h-5 text-white" />
                    </div>
                    {/* Online dot */}
                    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-green-400 border-2 border-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t("supportChat.title")}</p>
                    <p className="text-[11px] text-white/70">Online · Always here to help</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {messages.length > 0 && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/15 rounded-full"
                            onClick={clearHistory}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t("supportChat.clearHistory")}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-white/80 hover:text-white hover:bg-white/15 rounded-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="h-[360px] px-4" ref={scrollRef as any}>
                {messages.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-10 gap-3">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                      <Bot className="w-7 h-7 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">How can I help?</p>
                      <p className="text-xs text-muted-foreground mt-1 max-w-[240px]">
                        {t("supportChat.welcome")}
                      </p>
                    </div>
                  </div>
                )}

                <div className="py-4">
                  {messages.map((message) => (
                    <MessageBubble key={message.id} message={message} />
                  ))}
                </div>

              </ScrollArea>

              {/* Error */}
              {error && (
                <div className="px-4 py-2 text-xs text-red-500 flex items-center gap-2 border-t border-border/40">
                  <AlertCircle className="w-3.5 h-3.5" />
                  {error}
                </div>
              )}

              {/* Input */}
              <form
                onSubmit={handleSubmit}
                className="px-3 py-3 border-t border-border/40 flex items-center gap-2 bg-muted/20"
              >
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={t("supportChat.placeholder")}
                  disabled={isLoading}
                  maxLength={500}
                  className="flex-1 rounded-full bg-background border-border/60 text-sm h-9 px-4 focus-visible:ring-primary/30"
                />
                <Button
                  type="submit"
                  size="icon"
                  disabled={!inputValue.trim() || isLoading}
                  className="h-9 w-9 rounded-full flex-shrink-0 shadow-sm"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
